import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { __ } from "services/Translator";
import { setActiveRef, setView } from "stores/support/supportSlice";
import { selectSupportServing, selectSupportView } from "stores/support/supportSelectors";
import MessengerRoot from "support/panel/MessengerRoot";
import "support/styles/support.scss";

// Mode B: the same messenger core rendered as a full page inside the account
// area, at /dashboard/support/:view?/:ref?. It shares MessengerRoot with the
// slide-over, so a view written for one is automatically correct in the other.
//
// The URL is the source of truth here, not the slice, so a deep link into a
// specific thread survives a refresh, a share, and the AuthGuard swapping the
// entire route tree on login.
const VIEWS = ["home", "list", "conversation", "new", "kb", "rg"];

export default function SupportPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  const view = useSelector(selectSupportView);
  // A route is reachable by typing it, so the page has to check the serving
  // flag itself. Without this the messenger would run for anyone who knows the
  // URL while the backend still says it is off, which defeats both the dark
  // launch and any staged rollout of the flag.
  const serving = useSelector(selectSupportServing);

  const urlView = VIEWS.indexOf(params.view) !== -1 ? params.view : "home";
  const urlRef = params.ref || null;

  useEffect(() => {
    if (!serving) {
      return;
    }
    dispatch(setView(urlView));
    dispatch(setActiveRef(urlRef));
  }, [dispatch, urlView, urlRef, serving]);

  // Views navigate by writing to the slice; mirror that back into the address
  // bar so the back button behaves and the page can be linked to.
  useEffect(() => {
    if (!serving || view === urlView) {
      return;
    }
    const next = view === "home" ? "/dashboard/support" : "/dashboard/support/" + view;
    history.replace(next);
  }, [serving, view, urlView, history]);

  // null means /initial-settings has not answered yet. Waiting rather than
  // redirecting keeps a valid deep link from bouncing on a slow first paint.
  if (serving === null) {
    return (
      <div className="container-xxl supPage">
        <div className="supState" role="status">
          {__("Loading")}
        </div>
      </div>
    );
  }

  if (!serving) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <div className="container-xxl supPage">
      <h1 className="supPageTitle">{__("Support")}</h1>
      <div className="supPageBody">
        <MessengerRoot active />
      </div>
    </div>
  );
}
